import { useState } from 'react';
import { Modal, Box, Text, Paper, Grid, Stack, Accordion } from '@mantine/core';
import LogsTable from '../tables/LogsTable';
import ReenrollmentStatus from '../tables/ReenrollmentStatus';
import GuardiansList from '../tables/GuardiansList';

const StudentDetailModal = ({ 
  opened, 
  onClose, 
  row, 
  enrollments, 
  tableConfig, 
  handleReenrollmentStatusUpdate, 
  handleLogUpdate 
}) => {
  const [guardiansAccordionValue, setGuardiansAccordionValue] = useState(null);
  const [logsAccordionValue, setLogsAccordionValue] = useState(null);

  if (!row) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text weight={600} size="xl" color="#1a1b1e">
          Detalhes do Aluno: {row.original.name}
        </Text>
      }
      size="90%"
      centered
      styles={{
        modal: {
          height: '90vh',
          maxHeight: '90vh',
        },
        header: {
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e9ecef',
          padding: '16px 24px',
        },
        body: {
          padding: 0,
          height: 'calc(90vh - 80px)',
          overflow: 'hidden',
        },
        content: {
          height: '90vh',
          maxHeight: '90vh',
        }
      }}
    >
      <Box 
        sx={{
          width: '100%',
          height: '100%',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          overflow: 'auto',
        }}
      >
        <Paper 
          p="lg" 
          radius="md" 
          shadow="sm"
          sx={{
            backgroundColor: 'white',
            height: '100%',
            overflow: 'auto',
          }}
        >
          {/* Two Column Layout */}
          <Grid 
            gutter="xl"
            sx={{
              height: '100%',
              '@media (max-width: 768px)': {
                '& > div': {
                  width: '100% !important',
                  maxWidth: '100% !important',
                }
              }
            }}
          >
            {/* Left Container - Main Content */}
            <Grid.Col 
              span={6}
              sx={{
                '@media (max-width: 768px)': {
                  span: 12,
                }
              }}
            >
              <Stack spacing="sm">
                {/* Classes Section - Always visible */}
                <Box sx={{ flexShrink: 0 }}>
                  <Text 
                    weight={600} 
                    size="lg"
                    mb="md"
                    sx={{ 
                      color: '#1a1b1e',
                      borderBottom: '2px solid #e9ecef',
                      paddingBottom: '8px',
                    }}
                  >
                    Turmas do Aluno
                  </Text>
                  
                  {enrollments.length === 0 ? (
                    <Text 
                      size="sm"
                      color="dimmed"
                      sx={{ 
                        fontStyle: 'italic',
                        padding: '12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px dashed #dee2e6',
                        textAlign: 'center',
                      }}
                    >
                      Nenhuma turma encontrada
                    </Text>
                  ) : (
                    <Box 
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        '@media (max-width: 768px)': {
                          flexDirection: 'column',
                          gap: '8px',
                        }
                      }}
                    >
                      {enrollments.map((enrollment, index) => (
                        <Paper 
                          key={index}
                          p="sm"
                          radius="md"
                          sx={{ 
                            backgroundColor: '#e7f5ff',
                            border: '1px solid #339af0',
                            minWidth: '120px',
                          }}
                        >
                          <Text 
                            weight={500}
                            size="sm"
                            sx={{ color: '#1971c2' }}
                          >
                            {enrollment.student_class.name}
                          </Text>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Reenrollment Status - Right after Classes */}
                {tableConfig.columns && tableConfig.columns.reenrollment_status === true && (
                  <Box sx={{ flexShrink: 0 }}>
                    <Text 
                      weight={600} 
                      size="lg"
                      mb="md"
                      sx={{ 
                        color: '#1a1b1e',
                        borderBottom: '2px solid #e9ecef',
                        paddingBottom: '8px',
                      }}
                    >
                      Status de Rematrícula
                    </Text>
                    <ReenrollmentStatus 
                      row={row} 
                      onStatusUpdate={handleReenrollmentStatusUpdate} 
                    />
                  </Box>
                )}

                {/* Accordion for Guardians */}
                {tableConfig.showGuardians === true && (
                  <Box sx={{ flexShrink: 0 }}>
                    <Accordion 
                      value={guardiansAccordionValue} 
                      onChange={setGuardiansAccordionValue}
                      variant="contained"
                      radius="md"
                      sx={{
                        '& .mantine-Accordion-control': {
                          padding: '8px 12px',
                          backgroundColor: '#f8f9fa',
                          '&:hover': {
                            backgroundColor: '#e9ecef',
                          },
                        },
                        '& .mantine-Accordion-panel': {
                          padding: '6px',
                        },
                        '& .mantine-Accordion-item': {
                          border: '1px solid #dee2e6',
                          '&[data-active]': {
                            borderColor: '#339af0',
                          },
                        },
                      }}
                    >
                      <Accordion.Item value="guardians">
                        <Accordion.Control>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text size="lg">👥</Text>
                            <Text weight={500} size="md">Responsáveis</Text>
                          </Box>
                        </Accordion.Control>
                        <Accordion.Panel>
                          {guardiansAccordionValue === 'guardians' && (
                            <GuardiansList row={row} />
                          )}
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>
                  </Box>
                )}
              </Stack>
            </Grid.Col>

            {/* Right Container */}
            <Grid.Col 
              span={6}
              sx={{
                '@media (max-width: 768px)': {
                  span: 12,
                }
              }}
            >
              <Box 
                sx={{
                  borderLeft: '2px solid #e9ecef',
                  paddingLeft: '24px',
                  height: '100%',
                  '@media (max-width: 768px)': {
                    borderLeft: 'none',
                    borderTop: '2px solid #e9ecef',
                    paddingLeft: '0',
                    paddingTop: '24px',
                  }
                }}
              >
                {/* Comments Accordion */}
                {tableConfig.showLogs === true ? (
                  <Accordion 
                    value={logsAccordionValue} 
                    onChange={setLogsAccordionValue}
                    variant="contained"
                    radius="md"
                    sx={{
                      '& .mantine-Accordion-control': {
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        '&:hover': {
                          backgroundColor: '#e9ecef',
                        },
                      },
                      '& .mantine-Accordion-panel': {
                        padding: '6px',
                      },
                      '& .mantine-Accordion-item': {
                        border: '1px solid #dee2e6',
                        '&[data-active]': {
                          borderColor: '#339af0',
                        },
                      },
                    }}
                  >
                    <Accordion.Item value="logs">
                      <Accordion.Control>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Text size="lg">💬</Text>
                          <Text weight={500} size="md">Comentários</Text>
                        </Box>
                      </Accordion.Control>
                      <Accordion.Panel>
                        {logsAccordionValue === 'logs' && (
                          <LogsTable 
                            row={row} 
                            context={tableConfig.context}
                            onLogUpdate={handleLogUpdate}
                          />
                        )}
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                ) : (
                  /* Placeholder when no logs */
                  <Box 
                    sx={{
                      padding: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#adb5bd',
                      fontStyle: 'italic',
                      border: '1px dashed #dee2e6',
                      borderRadius: '8px',
                      backgroundColor: '#f8f9fa',
                    }}
                  >
                    <Text size="sm">Espaço reservado para comentários</Text>
                  </Box>
                )}
              </Box>
            </Grid.Col>
          </Grid>
        </Paper>
      </Box>
    </Modal>
  );
};

export default StudentDetailModal;
